import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { LoaderService } from '@app/shared/services/loader.service';
import { RoutingService } from '@app/shared/services/routing.service';
import { FeatureToggleService, RdrFeature } from '@app/ui/services/feature-toggle.service';
import { CognitoAuthService } from '@auth/services/cognito-auth.service';
import { AuthUser } from '@auth/user.model';
import { NavigationKey, SaveUserProfileMutationVariables } from '@generated/graphql';
import { Subject, concatMap, filter, map, of, take } from 'rxjs';
import { MyMonthlyStatsService } from '@app/pages/insights/services/my-monthly-stats.service';
import * as config from '@client-configs/header.json';
import { NavigationEnd, Params, Router } from '@angular/router';
import { UsersService } from '@app/pages/users/services/users.service';
import { LeadBoostService } from '@app/lead-boost/services/lead-boost/lead-boost.service';
import { ModalV2Service } from '@voyagu/radar-design-system';
import { NavigationService } from '@app/shared/services/navigation.service';
import { UntilDestroy, UntilDestroyed } from '@app/shared/utils/until-destroy';
import { BOARD_SHELLS, availableBoards } from '@app/pages/pipeline/pipeline-shells';

type HeaderLink = {
  link: string;
  label: string;
  queryParams: Params;
  group?: 'admin';
  navKey: NavigationKey;
  featureOn?: RdrFeature;
  featureOff?: RdrFeature;
};

type PipelineBoard = (typeof BOARD_SHELLS)[number];

const ADMIN_LABEL = 'Admin';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  RdrFeature = RdrFeature;

  @ViewChild('substitutionModalTpl') substitutionModalTpl: TemplateRef<unknown>;

  user: AuthUser;
  links: HeaderLink[];
  adminLinks: HeaderLink[];
  // The dropdown swallows `routerLinkActive`, so the button itself has to name the
  // admin screen the user is on.
  adminLabel = ADMIN_LABEL;
  adminChildActive = false;
  // EC-758: with two boards the "Pipeline" link becomes a dropdown in the same menu slot,
  // labelled with the board the user is on (or the default board elsewhere). Empty with one
  // board, which keeps the plain link.
  pipelineNavKey = NavigationKey.Pipeline;
  pipelineBoards: PipelineBoard[] = [];
  pipelineLabel = '';
  pipelineChildActive = false;
  activeBoardPath: string | null = null;
  canManageAgentSubstitution = false;
  canManageTeamSubstitution = false;
  canUseGlobalSearch = false;

  loading$: Subject<boolean>;

  agentStatus$ = this.leadBoostService.agentStatus$;
  teamMembersStatuses$ = this.leadBoostService.teamMembersStatuses();
  activeLeadBoostUserCount$ = this.leadBoostService.activeLeadBoostUserCount();

  constructor(
    public authService: CognitoAuthService,
    private loaderService: LoaderService,
    private routingService: RoutingService,
    private usersService: UsersService,
    private cdRef: ChangeDetectorRef,
    private leadBoostService: LeadBoostService,
    private featureToggleService: FeatureToggleService,
    private modalV2Service: ModalV2Service,
    private navigationService: NavigationService,
    private router: Router,
    private myMonthlyStatsService: MyMonthlyStatsService,
  ) {}

  get env() {
    try {
      const hostname = window.location.hostname;
      const prefix = hostname.split('.')[0].toLowerCase();

      return prefix === 'develop' ? 'Develop' : prefix === 'staging' ? 'Staging' : '';
    } catch (err) {
      return '';
    }
  }

  ngOnInit() {
    this.user = this.authService.user;

    this.setLinks([], []);
    this.authService
      .hasCognitoSession()
      .pipe(
        filter((loggedIn) => loggedIn),
        concatMap(() => this.navigationService.userAuth$),
        take(1),
        concatMap((userAuth) => {
          if (!this.featureToggleService.isFeatureEnabled(RdrFeature.monthly_stats)) {
            return of({ userAuth, hideTeamDashboard: false });
          }

          return this.myMonthlyStatsService.getAccess().pipe(
            map((access) => ({
              userAuth,
              hideTeamDashboard: !access.canView,
            })),
          );
        }),
      )
      .subscribe(({ userAuth, hideTeamDashboard }) => {
        const navigation = userAuth?.navigation ?? [];
        this.setLinks(
          (config.NAVIGATION as HeaderLink[]).filter((item) => {
            if (!navigation.includes(item.navKey)) {
              return false;
            }

            if (item.featureOn && !this.featureToggleService.isFeatureEnabled(item.featureOn)) {
              return false;
            }

            if (item.featureOff && this.featureToggleService.isFeatureEnabled(item.featureOff)) {
              return false;
            }

            if (hideTeamDashboard && item.navKey === NavigationKey.TeamDashboard) {
              return false;
            }

            return true;
          }),
          navigation,
        );
        this.canManageAgentSubstitution = navigation.includes(NavigationKey.AgentSubstitution);
        // Team-lead modal: BE emits AGENT_SUBSTITUTION_TEAM_MANAGE for SALES_TEAM_LEAD.
        // Cast until FE codegen picks up the new NavigationKey enum value.
        this.canManageTeamSubstitution = navigation.includes(
          'AGENT_SUBSTITUTION_TEAM_MANAGE' as NavigationKey,
        );
        this.canUseGlobalSearch = !!userAuth?.availableActions?.canUseGlobalSearch;
        this.cdRef.markForCheck();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        UntilDestroyed(this),
      )
      .subscribe(() => {
        this.syncAdminLabel();
        this.syncPipelineLabel();
        this.cdRef.markForCheck();
      });

    this.loading$ = this.loaderService.loading$;

    this.initAgentStatus();
  }

  setLinks(links: HeaderLink[], navigation: NavigationKey[]) {
    this.links = links.filter((item) => item.group !== 'admin');
    this.adminLinks = links.filter((item) => item.group === 'admin');

    const boards = availableBoards(navigation);

    this.pipelineBoards = boards.length > 1 ? boards : [];
    this.syncAdminLabel();
    this.syncPipelineLabel();
  }

  syncAdminLabel() {
    const path = this.router.url.split('?')[0];
    // Same prefix match as the `routerLinkActiveOptions` the dropdown items use.
    const activeLink = this.adminLinks.find((item) => path.startsWith(item.link));

    this.adminChildActive = !!activeLink;
    this.adminLabel = activeLink ? `${ADMIN_LABEL} / ${activeLink.label}` : ADMIN_LABEL;
  }

  syncPipelineLabel() {
    const path = this.router.url.split('?')[0];
    const onPipeline = path === '/pipeline' || path.startsWith('/pipeline/');
    const activeBoard = this.pipelineBoards.find(
      (board) => path === board.path || path.startsWith(`${board.path}/`),
    );
    // Boards come in BOARD_SHELLS order, which mirrors the backend's shell order, so the
    // first is the default board — the same one `PipelineAuthGuard` sends bare `/pipeline` to.
    const labelBoard = activeBoard ?? this.pipelineBoards[0];

    this.pipelineChildActive = onPipeline && this.pipelineBoards.length > 0;
    this.activeBoardPath = activeBoard?.path ?? null;
    this.pipelineLabel = labelBoard?.label ?? '';
  }

  initAgentStatus() {
    const isEnabledLeadBoost = this.featureToggleService.isFeatureEnabled(RdrFeature.lead_boost);

    if (isEnabledLeadBoost) {
      this.leadBoostService.agentStatus();
    }
  }

  setAgentAvailability(isActive: boolean) {
    this.leadBoostService.setAgentAvailability(
      { isActive },
      () => (this.activeLeadBoostUserCount$ = this.leadBoostService.activeLeadBoostUserCount()),
    );
  }

  logout() {
    void this.authService.logOut();
  }

  goToProfile() {
    this.routingService.navigateTo(['profile']);
  }

  openSubstitutionModal() {
    this.modalV2Service.showModal({ template: this.substitutionModalTpl });
  }

  setPhoto({ target }: Event) {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));

    const files = (target as HTMLInputElement).files;

    if (files) {
      const params: SaveUserProfileMutationVariables = {
        userId: this.authService.user.id,
        file: files[0] as unknown as string,
      };

      this.usersService.saveUserProfile(params).subscribe(({ data }) => {
        const ok = data?.saveUserProfile?.ok;

        if (ok) {
          const picture = data?.saveUserProfile?.result?.picture;

          this.user = {
            ...this.user,
            picture,
          };

          this.cdRef.detectChanges();
        }
      });
    }
  }
}
